import { FC, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Modal } from 'react-bootstrap';
import {
    ProcessButton,
    ProcessButtonStatus,
    Button,
    ButtonStyles,
    Icon,
    Label,
    TextInput,
    Toggle,
    Dropdown,
    Tooltip,
    Icons,
    ButtonSizes,
    GenericListInput
} from '@silverfort/sf-components';
import _ from 'lodash';
import AlertsOptionsToggles from '../../containers/AlertsManagement/components/AlertsOptionsToggles';
import {
    HTTP_OPTIONS,
    SECURITY_EVENTS_TYPES,
    PLATFORM_EVENTS_TYPES,
    AUTHENTICATION_EVENTS_TYPES,
    CLOUD_EVENTS_TYPES
} from '@silverfort-nodelibs/common/utils/consts';
import EventSettingsDrawer from '../../containers/AlertsManagement/components/EventSettingsDrawer';
import { NOTIFICATION_EVENTS_SETTINGS } from '../../containers/AlertsManagement/components/AlertsOptionsConsts';
import { URL_REGEX } from '@silverfort-nodelibs/common/utils/consts';
import { httpPost } from '../../../utils';
import {
    CustomIntegration,
    CustomIntegrationsSchema,
    FieldProperties,
    DropdownFieldProperties
} from '../../containers/AlertsManagement/types';
import { useIsCloudOnly } from '../../hooks/featureFlags/useIsCloudOnly';
import { getSasTokenExpiryDateString, isSasTokenExpired } from '@silverfort-nodelibs/common/utils/sasTokenUtils';

interface Errors {
    [key: string]: string;
}

const TEST_INTEGRATION_CONFIGURATION_TIMEOUT = 1000 * 5;
const TEST_INTEGRATION_CONFIGURATION_TIMEOUT_WITH_PROXY = 1000 * 30;

const mapTestPassedToTestStatus = (testPassed?: boolean) => {
    if (testPassed === undefined) {
        return 'initial';
    }

    return testPassed ? 'success' : 'failure';
};

interface CustomIntegrationDetailsProps {
    customIntegrationsSchema: CustomIntegrationsSchema;
    providerKey: string;
    data?: CustomIntegration;
    otherCustomIntegrations?: CustomIntegration[];
    providerName: string;
    logo: string;
    hasHttpProxy: boolean;
    onCancel: () => void;
    onSave: (customIntegration: CustomIntegration) => void;
}

interface WithDefaultInputProps extends CustomIntegrationDetailsProps {
    data: CustomIntegration;
}

const CustomIntegrationDetails: FC<WithDefaultInputProps> = ({
    customIntegrationsSchema,
    providerKey,
    data,
    otherCustomIntegrations = [],
    providerName,
    logo,
    hasHttpProxy,
    onCancel,
    onSave
}) => {
    const [customIntegrationData, setCustomIntegrationData] = useState<CustomIntegration>(data);
    const [supportedSecurityEventTypes, setSupportedSecurityEventTypes] = useState<typeof SECURITY_EVENTS_TYPES>();
    const [supportedPlatformEventTypes, setSupportedPlatformEventTypes] = useState<typeof PLATFORM_EVENTS_TYPES>();
    const [supportedAuthEventTypes, setSupportedAuthEventTypes] = useState<typeof AUTHENTICATION_EVENTS_TYPES>();
    const [supportedCloudEventTypes, setSupportedCloudEventTypes] = useState<typeof CLOUD_EVENTS_TYPES>();
    const [customizedEvents, setCustomizedEvents] = useState<Set<string>>();
    const [editableEvents, setEditableEvents] = useState<Record<string, boolean>>();
    const [eventInEdit, setEventInEdit] = useState();
    const [eventSelections, setEventSelections] = useState();
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [testStatus, setTestStatus] = useState<ProcessButtonStatus>('initial');
    const [testFailureMessage, setTestFailureMessage] = useState<string>(data.custom?.testFailureMessage ?? '');

    const [errors, setErrors] = useState<Errors>({});
    const [nameError, setNameError] = useState('');

    const isFirstRender = useRef(true);
    const isCloudOnly = useIsCloudOnly();

    const providerSchema = customIntegrationsSchema.properties[providerKey];
    const { documentationUrl, testable } = providerSchema;
    const { config: configSchema, name: nameFieldProperties } = providerSchema.items.properties;

    useEffect(() => {
        const supportedEvents = customIntegrationsSchema.properties[providerKey].supportedEvents;
        if (!supportedEvents) {
            return;
        }

        const pickSupportedEventsByTypes = (eventTypes: Record<string, any>) => {
            return _.pickBy(eventTypes, (event) => Object.keys(supportedEvents).includes(event.id));
        };

        const securityEvents = pickSupportedEventsByTypes(SECURITY_EVENTS_TYPES);
        const platformEvents = pickSupportedEventsByTypes(PLATFORM_EVENTS_TYPES);
        const authEvents = pickSupportedEventsByTypes(AUTHENTICATION_EVENTS_TYPES);
        const cloudEvents = pickSupportedEventsByTypes(CLOUD_EVENTS_TYPES);

        if (Object.keys(securityEvents).length) {
            setSupportedSecurityEventTypes(securityEvents);
        }
        if (Object.keys(platformEvents).length) {
            setSupportedPlatformEventTypes(platformEvents);
        }
        if (Object.keys(authEvents).length) {
            setSupportedAuthEventTypes(authEvents);
        }
        if (Object.keys(cloudEvents).length) {
            setSupportedCloudEventTypes(cloudEvents);
        }

        setCustomizedEvents(
            new Set(
                Object.entries(supportedEvents)
                    .filter(([eventId, eventProperties]) => eventProperties.isCustomizable)
                    .map(([eventId]) => eventId)
            )
        );

        setEditableEvents(
            Object.fromEntries(
                Object.entries(supportedEvents).map(([eventId, eventProperties]) => [
                    eventId,
                    eventProperties.isEditable !== false
                ])
            )
        );
    }, [providerKey, customIntegrationsSchema]);

    useEffect(() => {
        if (!_.isEqual(data.config, customIntegrationData.config)) {
            setTestStatus('initial');
            setTestFailureMessage('');
        } else {
            setTestStatus(mapTestPassedToTestStatus(data.custom?.testPassed));
            setTestFailureMessage(data.custom?.testFailureMessage ?? '');
        }
    }, [customIntegrationData.config, data]);

    const testConfiguration = async ({ shouldSendNotifications = false } = {}) => {
        const isIntegrationValid = validateIntegration();
        if (!isIntegrationValid) {
            return;
        }

        setTestStatus('loading');
        try {
            const requestBody = {
                integrations: {
                    [providerKey]: [customIntegrationData]
                },
                shouldSendNotifications
            };
            const { data }: any = await httpPost('/settings/testIntegrationConfiguration', requestBody, {
                [HTTP_OPTIONS.REQ_OPTIONS]: {
                    timeout: hasHttpProxy
                        ? TEST_INTEGRATION_CONFIGURATION_TIMEOUT_WITH_PROXY
                        : TEST_INTEGRATION_CONFIGURATION_TIMEOUT
                }
            });
            const integrationResponse = data[providerKey][0];

            if (integrationResponse?.httpStatus === 200) {
                setTestStatus('success');
                setTestFailureMessage('');
                return {
                    testPassed: true,
                    testFailureMessage: ''
                };
            }

            setTestStatus('failure');
            setTestFailureMessage(integrationResponse?.errorMessage || 'Test failed');
            return {
                testPassed: false,
                testFailureMessage: integrationResponse?.errorMessage || 'Test failed'
            };
        } catch (err: any) {
            setTestStatus('failure');
            setTestFailureMessage(err?.errorMessage || 'Test failed');
            return {
                testPassed: false,
                testFailureMessage: err?.errorMessage || 'Test failed'
            };
        }
    };

    const validateIntegrationName = (integrationName: string) => {
        if (!integrationName) {
            setNameError('Mandatory field');
            return false;
        }
        if (otherCustomIntegrations.find((customIntegration) => customIntegration.name === integrationName)) {
            setNameError('Name already exists');
            return false;
        }

        setNameError('');
        return true;
    };

    const validateMandatoryFields = () => {
        const newErrors: Record<string, string> = {};
        const isFieldEmpty = (value) => value == null || value === '';

        const validateField = (name: string, value: any, mandatory: boolean, path = name) => {
            if (mandatory && isFieldEmpty(value)) {
                _.set(newErrors, path, 'Mandatory field');
            }
        };

        Object.entries(configSchema.properties).forEach(([fieldName, fieldPropertiesRaw]) => {
            const fieldProperties = fieldPropertiesRaw;

            const isFieldMandatory = configSchema.required.includes(fieldName);
            const fieldValue = customIntegrationData.config?.[fieldName];

            if (fieldProperties.type === 'object') {
                if (isFieldMandatory || !isFieldEmpty(fieldValue)) {
                    fieldProperties.properties &&
                        Object.keys(fieldProperties.properties).forEach((nestedFieldName) => {
                            const isNestedFieldMandatory = fieldProperties.required?.includes(nestedFieldName) ?? false;
                            const nestedFieldValue = customIntegrationData.config?.[fieldName]?.[nestedFieldName];
                            validateField(
                                nestedFieldName,
                                nestedFieldValue,
                                isNestedFieldMandatory,
                                `${fieldName}.${nestedFieldName}`
                            );
                        });
                }
            } else {
                validateField(fieldName, fieldValue, isFieldMandatory);
            }
        });

        setErrors((prevErrors) => ({
            ...prevErrors,
            ...newErrors
        }));

        return Object.keys(newErrors).length === 0;
    };

    const validateIntegration = () => {
        const isNameValid = validateIntegrationName(customIntegrationData.name ?? '');
        const areMandatoryFieldsValid = validateMandatoryFields();
        return isNameValid && areMandatoryFieldsValid;
    };

    const save = async () => {
        const isIntegrationValid = validateIntegration();
        if (!isIntegrationValid) {
            return;
        }

        try {
            const testResult = testable ? await testConfiguration({ shouldSendNotifications: true }) : {};
            onSave({
                ...customIntegrationData,
                custom: {
                    ...customIntegrationData.custom,
                    ...testResult
                },
                lastModifiedDate: Date.now()
            });
        } catch (err: any) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                onSave: err.message
            }));
        }
    };

    const clearFieldError = (errorPath: string, parentName: string) => {
        setErrors((prevErr) => {
            const newErrors = { ...prevErr };
            _.unset(newErrors, errorPath);
            if (_.has(newErrors, parentName) && _.isEmpty(_.get(newErrors, parentName))) {
                _.unset(newErrors, parentName);
            }
            return newErrors;
        });
    };

    const basicSupportedEventTypes = [
        ...(supportedSecurityEventTypes ? [{ eventTypes: supportedSecurityEventTypes, title: 'Security Events' }] : []),
        ...(supportedPlatformEventTypes ? [{ eventTypes: supportedPlatformEventTypes, title: 'Platform Events' }] : []),
        ...(supportedAuthEventTypes ? [{ eventTypes: supportedAuthEventTypes, title: 'Authentication Events' }] : [])
    ];

    const supportedEventToggles = [
        ...(isCloudOnly ? [] : basicSupportedEventTypes),
        ...(supportedCloudEventTypes ? [{ eventTypes: supportedCloudEventTypes, title: 'Cloud Events' }] : [])
    ];

    const addFieldError = (errorPath: string, errorMessage: string) => {
        setErrors((prevErrors) => _.set({ ...prevErrors }, errorPath, errorMessage));
    };

    const hasIntegrationErrors =
        Object.keys(errors).filter((errorKey) => errorKey !== 'onSave').length > 0 || nameError !== '';

    const buildTextInput = (
        fieldName: string,
        fieldProperties: FieldProperties,
        parentName = '',
        parentFieldProperties?: FieldProperties
    ) => {
        const parentPath = parentName ? `config.${parentName}` : 'config';
        const fieldPath = `${parentPath}.${fieldName}`;
        const errorPath = parentName ? `${parentName}.${fieldName}` : fieldName;

        const validateString = (value: string, format?: string) => {
            const requiredFields = parentFieldProperties ? parentFieldProperties.required : configSchema.required;
            if (value === '' && requiredFields?.includes(fieldName)) {
                addFieldError(errorPath, 'Mandatory field');
            } else if (format === 'uri' && !URL_REGEX.test(value)) {
                addFieldError(errorPath, 'Invalid URL');
            } else if (format === 'sastoken' && isSasTokenExpired(value)) {
                addFieldError(errorPath, 'Token expired ' + getSasTokenExpiryDateString(value));
            } else {
                clearFieldError(errorPath, parentName);
            }
        };

        let value = _.get(customIntegrationData, fieldPath, '');

        if (isFirstRender.current && value === '') {
            value = (fieldProperties.defaultValue as string) ?? '';
        }

        if (value && _.get(customIntegrationData, fieldPath) !== value) {
            setCustomIntegrationData((prevData) => _.set(_.cloneDeep(prevData), fieldPath, value));
        }
        const validationMessage = _.get(errors, errorPath);

        return (
            <div className="w-80">
                <TextInput
                    placeholder={fieldProperties.example}
                    value={value}
                    isPassword={fieldProperties.format === 'password'}
                    isSasToken={fieldProperties.format === 'sastoken'}
                    onChange={(val) => {
                        validateString(val, fieldProperties.format);
                        setCustomIntegrationData((prevData) => _.set(_.cloneDeep(prevData), fieldPath, val));
                    }}
                    validationMessage={validationMessage}
                    className="w-80"
                />
            </div>
        );
    };

    const buildIntegerInput = (
        fieldName: string,
        fieldProperties: FieldProperties,
        parentName = '',
        parentFieldProperties?: FieldProperties
    ) => {
        const parentPath = parentName ? `config.${parentName}` : 'config';
        const fieldPath = `${parentPath}.${fieldName}`;
        const errorPath = parentName ? `${parentName}.${fieldName}` : fieldName;

        const validateNumber = (value: string) => {
            const requiredFields = parentFieldProperties ? parentFieldProperties.required : configSchema.required;
            if (value === '' && requiredFields?.includes(fieldName)) {
                addFieldError(errorPath, `Mandatory field`);
            }
            const valueAsNumber = parseInt(value);

            if (fieldProperties.minimum !== undefined && valueAsNumber < fieldProperties.minimum) {
                addFieldError(errorPath, `Value should be greater than ${fieldProperties.minimum}`);
            } else if (fieldProperties.maximum !== undefined && valueAsNumber > fieldProperties.maximum) {
                addFieldError(errorPath, `Value should be less than ${fieldProperties.maximum}`);
            } else {
                clearFieldError(errorPath, parentName);
            }

            return valueAsNumber;
        };

        let value = _.get(customIntegrationData, fieldPath, '');

        if (isFirstRender.current && value === '') {
            value = (fieldProperties.defaultValue as string) ?? '';
        }

        if (value && _.get(customIntegrationData, fieldPath) !== value) {
            setCustomIntegrationData((prevData) => _.set(_.cloneDeep(prevData), fieldPath, value));
        }
        const validationMessage = _.get(errors, errorPath);

        return (
            <div className="w-80">
                <TextInput
                    placeholder={fieldProperties.example}
                    value={value}
                    numbersOnly
                    step={1}
                    {...(fieldProperties.minimum !== undefined && { min: fieldProperties.minimum })}
                    {...(fieldProperties.maximum !== undefined && { max: fieldProperties.maximum })}
                    onChange={(val) => {
                        setCustomIntegrationData((prevData) => {
                            const newData = _.cloneDeep(prevData);
                            if (val) {
                                const valAsNumber = validateNumber(val);
                                _.set(newData, fieldPath, valAsNumber);
                            } else {
                                _.unset(newData, fieldPath);
                            }
                            return newData;
                        });
                    }}
                    validationMessage={validationMessage}
                    className="w-80"
                />
            </div>
        );
    };

    const buildStringsArrayInput = (fieldName: string, fieldProperties: FieldProperties, parentName = '') => {
        const parentPath = parentName ? `config.${parentName}` : 'config';
        const fieldPath = `${parentPath}.${fieldName}`;

        let items = _.get(customIntegrationData, fieldPath, []) as string[];

        if (isFirstRender.current && !items.length) {
            items = (fieldProperties.defaultValue as string[]) || [];
        }

        if (items.length && _.get(customIntegrationData, fieldPath) !== items) {
            setCustomIntegrationData((prevData) => _.set(_.cloneDeep(prevData), fieldPath, items));
        }

        return (
            <div className="w-80">
                <GenericListInput
                    items={items}
                    setItems={(newItems) => {
                        setCustomIntegrationData((prevData) => _.set(_.cloneDeep(prevData), fieldPath, newItems));
                    }}
                    className="w-80"
                    placeholder={fieldProperties.example || 'Enter items'}
                />
            </div>
        );
    };

    const buildDropdown = (
        fieldName: string,
        fieldProperties: DropdownFieldProperties,
        parentName = '',
        parentFieldProperties?: FieldProperties
    ) => {
        const parentPath = parentName ? `config.${parentName}` : 'config';
        const fieldPath = `${parentPath}.${fieldName}`;
        const errorPath = parentName ? `${parentName}.${fieldName}` : fieldName;
        const requiredFields = parentFieldProperties ? parentFieldProperties.required : configSchema.required;
        const isRequired = requiredFields?.includes(fieldName);

        const items = fieldProperties.enum.map((enumValue) => ({
            title: enumValue,
            value: enumValue
        }));

        let selectedItems = _.get(customIntegrationData, fieldPath, [] as string[]);

        if (isFirstRender.current && !selectedItems.length) {
            selectedItems = (fieldProperties.defaultValue as string[]) || [];
        }

        if (selectedItems && _.get(customIntegrationData, fieldPath) !== selectedItems) {
            setCustomIntegrationData((prevData) => _.set(_.cloneDeep(prevData), fieldPath, selectedItems));
        }
        const validationMessage = _.get(errors, errorPath);
        const removable = !isRequired && !!_.get(customIntegrationData, fieldPath);

        return (
            <Dropdown
                items={items}
                variant="trigger"
                selectedItems={selectedItems}
                removable={removable}
                onRemove={() => {
                    setCustomIntegrationData((prevData) => {
                        const newData = _.cloneDeep(prevData);
                        _.unset(newData, fieldPath);
                        const parentValue = newData.config?.[parentName];
                        if (
                            typeof parentValue === 'object' &&
                            parentValue !== null &&
                            Object.keys(parentValue).length === 0
                        ) {
                            delete newData.config?.[parentName];
                        }

                        return newData;
                    });
                }}
                onChange={(value) => {
                    setCustomIntegrationData((prevData) => _.set(_.cloneDeep(prevData), fieldPath, value));
                    clearFieldError(errorPath, parentName);
                }}
                validationMessage={validationMessage}
            />
        );
    };

    const buildToggle = (fieldName: string, fieldProperties: FieldProperties, parentName = '') => {
        const parentPath = parentName ? `config.${parentName}` : 'config';
        const fieldPath = `${parentPath}.${fieldName}`;

        let checked = _.get(customIntegrationData, fieldPath) as boolean | undefined;

        if (isFirstRender.current && checked === undefined) {
            checked = (fieldProperties.defaultValue as boolean) ?? false;
        }

        if (_.get(customIntegrationData, fieldPath) !== checked) {
            setCustomIntegrationData((prevData) => _.set(_.cloneDeep(prevData), fieldPath, checked));
        }

        return (
            <Toggle
                checked={checked}
                onChange={() => {
                    setCustomIntegrationData((prevData) => {
                        const newValue = !_.get(prevData, fieldPath);
                        return _.set(_.cloneDeep(prevData), fieldPath, newValue);
                    });
                }}
            />
        );
    };

    const buildDynamicField = (
        fieldName: string,
        fieldProperties: FieldProperties,
        parentName?: string,
        parentFieldProperties?: FieldProperties
    ) => {
        if (fieldProperties.type === 'string' && fieldProperties.enum) {
            return buildDropdown(
                fieldName,
                fieldProperties as DropdownFieldProperties,
                parentName,
                parentFieldProperties
            );
        }

        if (fieldProperties.type === 'string') {
            return buildTextInput(fieldName, fieldProperties, parentName, parentFieldProperties);
        }

        if (fieldProperties.type === 'boolean') {
            return buildToggle(fieldName, fieldProperties, parentName);
        }

        if (fieldProperties.type === 'integer') {
            return buildIntegerInput(fieldName, fieldProperties, parentName, parentFieldProperties);
        }

        if (fieldProperties.type === 'array' && fieldProperties.items?.type === 'string') {
            return buildStringsArrayInput(fieldName, fieldProperties, parentName);
        }

        return <></>;
    };

    const buildNestedObjectField = (objectFieldName: string, objectFieldProperties: FieldProperties) => {
        if (!objectFieldProperties.properties) return <></>;

        return (
            <div className="border-l border-sf-gray-100">
                {Object.entries(objectFieldProperties.properties).map(([fieldName, fieldProperties]) => (
                    <div key={fieldName} className="flex p-2 items-center">
                        <div className="w-1/5 h-10 flex items-center gap-1">
                            <Label
                                text={fieldProperties.displayName}
                                mandatory={objectFieldProperties.required?.includes(fieldName)}
                            />
                            <div>
                                <Tooltip onChange={() => {}} title={fieldProperties.description} width={300}>
                                    <Icon icon={Icons.Info} size={14} />
                                </Tooltip>
                            </div>
                        </div>
                        <div className="w-4/5 h-10">
                            {buildDynamicField(fieldName, fieldProperties, objectFieldName, objectFieldProperties)}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const fieldsToRender = Object.entries(configSchema.properties).map(([fieldName, fieldProperties]) => (
        <div key={fieldName} className="flex p-2 items-start">
            <div className="w-1/5 h-10 flex items-center gap-1">
                <Label text={fieldProperties.displayName} mandatory={configSchema.required.includes(fieldName)} />
                <div>
                    <Tooltip onChange={() => {}} title={fieldProperties.description} width={300}>
                        <Icon icon={Icons.Info} size={14} />
                    </Tooltip>
                </div>
            </div>
            <div className="w-4/5 min-h-10">
                {fieldProperties.type === 'object'
                    ? buildNestedObjectField(fieldName, fieldProperties)
                    : buildDynamicField(fieldName, fieldProperties)}
            </div>
        </div>
    ));

    isFirstRender.current = false;

    return (
        <div className="p-6 border border-sf-gray-100 rounded-lg">
            <div className="mb-4 flex items-center bg-sf-gray-50 pl-6 h-20 border border-sf-gray-100 rounded-lg">
                <img alt="integration logo" className="w-9 h-9" src={logo} />
                <div className="ml-2 text-sm">
                    <div className="font-medium text-sf-gray-800">{providerName}</div>
                    <div className="font-normal text-sf-gray-500">
                        For more information about this integration and the required prerequisites, see
                        <a
                            className="ml-1 text-sf-primary-500"
                            href={documentationUrl}
                            target="_blank"
                            rel="noopener noreferrer">
                            here
                        </a>
                        .
                    </div>
                </div>
            </div>
            <div className="flex p-2 items-center">
                <div className="w-1/5 h-10 flex items-center gap-1">
                    <Label text="Name" mandatory={true} />
                    <div>
                        <Tooltip onChange={() => {}} title={nameFieldProperties.description} width={300}>
                            <Icon icon={Icons.Info} size={14} />
                        </Tooltip>
                    </div>
                </div>
                <div className="w-4/5 h-10">
                    <TextInput
                        validationMessage={nameError}
                        placeholder="Integration Name"
                        value={customIntegrationData.name}
                        onChange={(val) => {
                            validateIntegrationName(val);
                            setCustomIntegrationData({ ...customIntegrationData, name: val });
                        }}
                        className="w-80"
                    />
                </div>
            </div>
            <hr className="my-6 border border-gray-100" />
            {fieldsToRender}
            {supportedEventToggles.length > 0 && (
                <div className="flex p-2 items-center-2">
                    <div className="w-1/5 flex h-10 items-center">
                        <Label text="Events" />
                    </div>
                    <div className="flex w-4/5 flex-col mt-0.5">
                        {supportedEventToggles.map((eventToggle) => (
                            <AlertsOptionsToggles
                                key={eventToggle.title}
                                className="min-h-22"
                                data={customIntegrationData}
                                setData={setCustomIntegrationData}
                                eventInEdit={eventInEdit}
                                setEventInEdit={setEventInEdit}
                                customizedEvents={customizedEvents}
                                eventsTypes={eventToggle.eventTypes}
                                title={eventToggle.title}
                                editableEvents={editableEvents || {}}
                            />
                        ))}
                    </div>
                </div>
            )}
            <div className="flex justify-between items-center h-8">
                <ProcessButton
                    disabled={!testable || hasIntegrationErrors}
                    status={testStatus}
                    text="Test Connectivity"
                    onClick={() => testConfiguration()}
                    onRetry={testConfiguration}
                    loadingMessage="Testing Connectivity"
                    onCancel={() => setTestStatus('initial')}
                    successMessage="Successful"
                    failureMessage="Connectivity test failed"
                    failureInfo={testFailureMessage}
                    containerSize={ButtonSizes.Medium}
                    failureClassName="w-[250px]"
                    loadingClassName="w-[250px]"
                    successClassName="w-[250px]"
                />
                <div className="flex justify-end">
                    <Button
                        text="Cancel"
                        buttonStyle={ButtonStyles.TertiarySolid}
                        onClick={() =>
                            !_.isEqual(data, customIntegrationData) ? setShowCancelModal(true) : onCancel()
                        }
                        className="mr-1"
                    />
                    <Button
                        text="Save"
                        disabled={_.isEqual(data, customIntegrationData) || hasIntegrationErrors}
                        onClick={save}
                    />
                </div>
            </div>
            {errors.onSave && <div className="text-red-500 text-sm mt-2">{errors.onSave}</div>}
            <EventSettingsDrawer
                data={customIntegrationData}
                eventInEdit={eventInEdit}
                eventSelections={eventSelections}
                eventSettings={NOTIFICATION_EVENTS_SETTINGS[eventInEdit]}
                setEventSelections={setEventSelections}
                setEventInEdit={setEventInEdit}
                setData={setCustomIntegrationData}
            />
            {showCancelModal && (
                <Modal
                    className="font-sf flex flex-col justify-center"
                    id="cancelModal"
                    dialogClassName="w-[360px] [&_.modal-content]:rounded-lg [&_.modal-content]:shadow-modal-light"
                    show
                    onHide={() => setShowCancelModal(false)}
                    keyboard={false}
                    backdrop="static">
                    <Modal.Header className="border-hidden pt-4 pr-6 pb-0 pl-0" closeButton>
                        <div className="px-4 pb-4 mb-1 text-14 font-medium text-sf-gray-800">Discard changes?</div>
                    </Modal.Header>
                    <Modal.Body className=" text-13 px-4 pb-6 h-full border-t border-b">
                        Any updates you made will be lost.
                    </Modal.Body>
                    <Modal.Footer className="border-hidden px-6">
                        <div className="flex justify-end items-center">
                            <Button
                                onClick={() => setShowCancelModal(false)}
                                text="No"
                                buttonStyle={ButtonStyles.TertiarySolid}
                                className="mr-2"
                            />
                            <Button onClick={onCancel} text="Yes" />
                        </div>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
};

const withDefaultInput = (Component: any) => (props: CustomIntegrationDetailsProps) => {
    const { customIntegrationsSchema, providerKey, data } = props;

    const defaultProps = {
        ...props
    };

    const DEFAULT_CUSTOM_INTEGRATION: CustomIntegration = {
        id: uuidv4(),
        name: '',
        config: {},
        creationDate: Date.now(),
        lastModifiedDate: Date.now(),
        enabled: true
    };

    if (!data) {
        const settings = {};
        const activeEvents = {};

        const supportedEvents = customIntegrationsSchema.properties[providerKey].supportedEvents ?? {};
        Object.entries(supportedEvents).forEach(([eventId, eventProperties]) => {
            if (eventProperties.defaultValue) {
                activeEvents[eventId] = true;
            }
            if (eventProperties.isCustomizable) {
                settings[eventId] =
                    eventProperties.defaultCustomValues ?? NOTIFICATION_EVENTS_SETTINGS[eventId].defaultSelections;
            }
        });

        defaultProps.data = {
            ...DEFAULT_CUSTOM_INTEGRATION,
            settings,
            activeEvents
        };
    }

    return <Component {...defaultProps} />;
};

export default withDefaultInput(CustomIntegrationDetails);
